import { mediaAccessIssueCookies } from "./api/generated/sASOnlineClinicBackendAPI";
import { messages } from "../utils/i18n/messages";
import { getStatus } from "../utils/http/getStatus";

const mediaBaseUrlRaw = import.meta.env.VITE_MEDIA_BASE_URL ?? "";
const mediaBaseUrl = mediaBaseUrlRaw.replace(/\/$/, "");
const protectedMediaPrefixes = ["patients/", "uploads/"] as const;

function isAbsoluteUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://");
}

function isProtectedMediaPath(path: string): boolean {
  return protectedMediaPrefixes.some((prefix) => path.startsWith(`/${prefix}`));
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizeProtectedMediaPath(path: string): string | null {
  const withoutQuery = path.split(/[?#]/, 1)[0];
  if (isProtectedMediaPath(withoutQuery)) {
    return withoutQuery;
  }

  let candidate = withoutQuery;
  for (const prefix of protectedMediaPrefixes) {
    if (candidate.startsWith(prefix)) {
      candidate = `/${candidate}`;
      break;
    }
  }

  if (!isProtectedMediaPath(candidate)) {
    for (const prefix of protectedMediaPrefixes) {
      const slashSegmentIndex = candidate.indexOf(`/${prefix}`);
      if (slashSegmentIndex >= 0) {
        candidate = candidate.slice(slashSegmentIndex);
        break;
      }
      const bareSegmentIndex = candidate.indexOf(prefix);
      if (bareSegmentIndex >= 0) {
        candidate = `/${candidate.slice(bareSegmentIndex)}`;
        break;
      }
    }
  }

  if (isProtectedMediaPath(candidate)) {
    return candidate;
  }
  return null;
}

export function toMediaResourcePath(urlOrPath?: string | null): string | null {
  if (!urlOrPath) {
    return null;
  }
  const v = urlOrPath.trim();
  if (!v) {
    return null;
  }
  const normalized = normalizeProtectedMediaPath(v);
  if (normalized) {
    return normalized;
  }
  if (!isAbsoluteUrl(v)) {
    const decoded = safeDecodeURIComponent(v);
    if (decoded !== v) {
      const decodedNormalized = normalizeProtectedMediaPath(decoded);
      if (decodedNormalized) {
        return decodedNormalized;
      }
    }
    return null;
  }
  try {
    const u = new URL(v);
    const pathnameNormalized = normalizeProtectedMediaPath(u.pathname);
    if (pathnameNormalized) {
      return pathnameNormalized;
    }

    for (const [, rawValue] of u.searchParams.entries()) {
      const directNormalized = normalizeProtectedMediaPath(rawValue);
      if (directNormalized) {
        return directNormalized;
      }
      const decodedValue = safeDecodeURIComponent(rawValue);
      if (decodedValue !== rawValue) {
        const decodedNormalized = normalizeProtectedMediaPath(decodedValue);
        if (decodedNormalized) {
          return decodedNormalized;
        }
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function preferMediaTarget(
  resourcePath?: string | null,
): string | undefined {
  const rp = resourcePath?.trim();
  return rp || undefined;
}

function resolveMediaUrl(resourcePath: string): string {
  if (mediaBaseUrl) {
    return `${mediaBaseUrl}${resourcePath}`;
  }
  return resourcePath;
}

async function issueCookie(resourcePath: string): Promise<void> {
  const result = await mediaAccessIssueCookies({ resourcePath });
  if (result.status !== 204) {
    throw new Error("failed to issue media cookie");
  }
}

function getMediaAccessErrorMessage(e: unknown): string {
  const status = getStatus(e);
  if (status === 403) {
    return messages.FORBIDDEN_ACCESS;
  }
  if (status === 429) {
    return messages.MEDIA_COOKIE_RATE_LIMIT;
  }
  return messages.SERVER_OR_NETWORK_ERROR;
}

function notifyMediaAccessError(e: unknown): void {
  // Global fallback notification for places that trigger media open without local error state.
  if (typeof window !== "undefined" && typeof window.alert === "function") {
    window.alert(getMediaAccessErrorMessage(e));
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function openProtectedMedia(
  urlOrPath?: string | null,
): Promise<void> {
  if (!urlOrPath) {
    return;
  }

  const resourcePath = toMediaResourcePath(urlOrPath);
  if (!resourcePath) {
    return;
  }

  // Open a blank tab synchronously so popup blockers do not reject the later navigation.
  const preOpened =
    typeof window !== "undefined" ? window.open("", "_blank") : null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await issueCookie(resourcePath);
      const targetUrl = resolveMediaUrl(resourcePath);
      if (preOpened && !preOpened.closed) {
        try {
          preOpened.opener = null;
        } catch {
          // noop
        }
        preOpened.location.href = targetUrl;
      } else {
        window.open(targetUrl, "_blank", "noopener,noreferrer");
      }
      return;
    } catch (error) {
      if (attempt === 1) {
        if (preOpened && !preOpened.closed) {
          preOpened.close();
        }
        notifyMediaAccessError(error);
        return;
      }
      await sleep(100);
    }
  }
}

export async function fetchProtectedMediaBlob(
  urlOrPath?: string | null,
): Promise<Blob> {
  const resourcePath = toMediaResourcePath(urlOrPath);
  if (!resourcePath) {
    throw new Error("protected media path is required");
  }

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await issueCookie(resourcePath);
      const targetUrl = resolveMediaUrl(resourcePath);
      const response = await fetch(targetUrl, { credentials: "include" });
      if (!response.ok) {
        throw new Error("failed to fetch protected media");
      }
      return await response.blob();
    } catch (error) {
      if (attempt === 1) {
        throw error;
      }
      await sleep(100);
    }
  }

  throw new Error("failed to fetch protected media");
}
